import { getDataClient } from "@/lib/dataClient";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";
import { curriculumForGrade, type CurriculumSubject } from "@/data/curriculum";
import { extractGradeLevel } from "@/lib/profile";

export interface ScheduleSlot {
  time: string;
  subject: string;
  type: "ders" | "canli" | "test";
  /** Öğrencinin takvimine kayıtlı olanlar "saved", sistemin önerdiği rota "generated" olur. */
  source?: "saved" | "generated";
  href?: string;
}

export interface ScheduleDay {
  day: string;
  slots: ScheduleSlot[];
}

export interface Homework {
  id: string;
  title: string;
  subject: string;
  teacher: string;
  dueDate: string;
  status: "bekliyor" | "tamamlandi" | "gecikti";
  description: string;
}

export interface Note {
  id: string;
  title: string;
  subject: string;
  content: string;
  date: string;
}

export interface FavoriteItem {
  id: string;
  type: "video" | "soru" | "konu";
  title: string;
  subject: string;
  meta: string;
}

export interface Certificate {
  id: string;
  title: string;
  description: string;
  date: string;
  icon: string;
  color: string;
}

export interface WrongQuestion {
  id: string;
  subject: string;
  topic: string;
  question: string;
  correctAnswer: string;
  myAnswer: string;
  date: string;
}

const WEEK_DAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const fallbackSubjects: CurriculumSubject[] = [
  { name: "Türkçe", icon: "ri-font-size", color: "" },
  { name: "Matematik", icon: "ri-function-line", color: "" },
  { name: "Fen Bilimleri", icon: "ri-flask-line", color: "" },
  { name: "İngilizce", icon: "ri-english-input", color: "" },
  { name: "Sosyal Bilgiler", icon: "ri-global-line", color: "" },
];

function uniqueSubjects(subjects: string[], curriculum: CurriculumSubject[]) {
  const indexed = new Map(curriculum.map((subject) => [subject.name.toLocaleLowerCase("tr-TR"), subject]));
  const priority = subjects
    .map((name) => indexed.get(name.toLocaleLowerCase("tr-TR")))
    .filter((subject): subject is CurriculumSubject => Boolean(subject));
  const seen = new Set<string>();
  return [...priority, ...curriculum].filter((subject) => {
    if (seen.has(subject.name)) return false;
    seen.add(subject.name);
    return true;
  });
}

/**
 * Bu veriler veritabanına yazılmaz: öğrenci henüz program oluşturmamışsa
 * ekranda, seviyesine uygun ve doğrudan başlatılabilir bir başlangıç rotası gösterir.
 */
function buildStarterSchedule(grade: string | null, weakSubjects: string[]): ScheduleDay[] {
  const curriculum = curriculumForGrade(grade);
  const ordered = uniqueSubjects(weakSubjects, curriculum.length ? curriculum : fallbackSubjects);
  const subjects = ordered.length ? ordered : fallbackSubjects;
  const weekdayTimes = ["17:00", "17:30"];
  const weekendTimes = ["10:00", "10:30"];

  return WEEK_DAYS.map((day, dayIndex) => {
    const first = subjects[(dayIndex * 2) % subjects.length];
    const second = subjects[(dayIndex * 2 + 1) % subjects.length];
    const isWeekend = dayIndex >= 5;
    const firstIsTest = dayIndex === 2 || dayIndex === 5;
    const secondIsTest = dayIndex === 0 || dayIndex === 3 || dayIndex === 6;
    const time = isWeekend ? weekendTimes : weekdayTimes;
    const toSlot = (subject: CurriculumSubject, at: string, isTest: boolean): ScheduleSlot => ({
      time: at,
      subject: isTest ? `${subject.name} · Kısa değerlendirme` : `${subject.name} · Konu çalışması`,
      type: isTest ? "test" : "ders",
      source: "generated",
      href: isTest ? "/ogrenci/soru-bankasi" : "/ogrenci/video-dersler",
    });

    return {
      day,
      slots: [toSlot(first, time[0], firstIsTest), toSlot(second, time[1], secondIsTest)],
    };
  });
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
}

export function useSchedule() {
  return useDataQuery<ScheduleDay[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return WEEK_DAYS.map((day) => ({ day, slots: [] }));

    const [{ data, error }, authResult, weakResult] = await Promise.all([
      dataClient.from("schedule_slots").select("day, time, subject, type").eq("user_id", userId),
      dataClient.auth.getUser(),
      dataClient
        .from("weak_topics")
        .select("subject")
        .eq("user_id", userId)
        .order("mastery_percent", { ascending: true })
        .limit(5),
    ]);

    if (error) throw error;

    const byDay = new Map<string, ScheduleSlot[]>();
    (data ?? []).forEach((s: { day: string; time: string; subject: string; type: ScheduleSlot["type"] }) => {
      const list = byDay.get(s.day) ?? [];
      list.push({ time: s.time, subject: s.subject, type: s.type, source: "saved" });
      byDay.set(s.day, list);
    });

    const grade = extractGradeLevel(String(authResult.data.user?.user_metadata?.sinif ?? ""));
    const weakSubjects = (weakResult.data ?? []).map((item: { subject: string }) => item.subject);
    const starterSchedule = buildStarterSchedule(grade, weakSubjects);

    return starterSchedule.map((generatedDay) => {
      const savedSlots = byDay.get(generatedDay.day) ?? [];
      // Öğretmen veya öğrenci tarafından tanımlanan saatler her zaman önce gösterilir.
      // Kalan akış, boş gün bırakmamak için sınıf düzeyine uygun rota ile tamamlanır.
      return { ...generatedDay, slots: [...savedSlots, ...generatedDay.slots] };
    });
  });
}

export function useHomework() {
  const query = useDataQuery<Homework[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("homework")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((h: { id: string; title: string; subject: string; teacher_name: string | null; due_date: string | null; status: string; description: string | null }) => ({
      id: h.id,
      title: h.title,
      subject: h.subject,
      teacher: h.teacher_name ?? "",
      dueDate: h.due_date ?? "",
      status: h.status as Homework["status"],
      description: h.description ?? "",
    }));
  });

  const markDone = async (id: string) => {
    const dataClient = getDataClient();
    await dataClient.from("homework").update({ status: "tamamlandi" }).eq("id", id);
    query.reload();
  };

  return { ...query, markDone };
}

export function useNotes() {
  const query = useDataQuery<Note[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("notes")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((n: { id: string; title: string; subject: string; content: string | null; created_at: string }) => ({
      id: n.id,
      title: n.title,
      subject: n.subject,
      content: n.content ?? "",
      date: shortDate(n.created_at),
    }));
  });

  const addNote = async (title: string, subject: string, content: string) => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return;
    await dataClient.from("notes").insert({ user_id: userId, title, subject, content });
    query.reload();
  };

  const deleteNote = async (id: string) => {
    const dataClient = getDataClient();
    await dataClient.from("notes").delete().eq("id", id);
    query.reload();
  };

  return { ...query, addNote, deleteNote };
}

export function useFavorites() {
  const query = useDataQuery<FavoriteItem[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("favorites")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((f: { id: string; type: string; title: string; subject: string | null; meta: string | null }) => ({
      id: f.id,
      type: f.type as FavoriteItem["type"],
      title: f.title,
      subject: f.subject ?? "",
      meta: f.meta ?? "",
    }));
  });

  const removeFavorite = async (id: string) => {
    const dataClient = getDataClient();
    await dataClient.from("favorites").delete().eq("id", id);
    query.reload();
  };

  return { ...query, removeFavorite };
}

export function useCertificates() {
  return useDataQuery<Certificate[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("user_certificates")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((c: { id: string; title: string; description: string | null; earned_date: string | null; icon: string | null; color: string | null }) => ({
      id: c.id,
      title: c.title,
      description: c.description ?? "",
      date: c.earned_date ?? "",
      icon: c.icon ?? "ri-award-line",
      color: c.color ?? "bg-accent-100 text-accent-700",
    }));
  });
}

export function useWrongQuestions() {
  const query = useDataQuery<WrongQuestion[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("wrong_questions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((q: { id: string; subject: string; topic: string; question: string; correct_answer: string | null; my_answer: string | null; created_at: string }) => ({
      id: q.id,
      subject: q.subject,
      topic: q.topic,
      question: q.question,
      correctAnswer: q.correct_answer ?? "",
      myAnswer: q.my_answer ?? "",
      date: shortDate(q.created_at),
    }));
  });

  const resolveQuestion = async (id: string) => {
    const dataClient = getDataClient();
    await dataClient.from("wrong_questions").delete().eq("id", id);
    query.reload();
  };

  return { ...query, resolveQuestion };
}
